import React, { useRef, useCallback, forwardRef } from 'react';
import { VariableSizeList as List } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import classnames from 'classnames/bind';
import { isEmpty } from 'lodash';
import { useIntl } from '../../../../../../modules/taboola-common-frontend-modules/i18n';
import DropdownLoader from '../../../../../DropdownLoader/DropdownLoader';
import RetryMenuItem from '../../../../../RetryMenuItem/RetryMenuItem';
import { useDropdownData } from '../../hooks/useDropdownData';
import { useDropdownNavigation } from '../../hooks/useDropdownNavigation';
import { useDropdownRenderer } from '../../hooks/useDropdownRenderer';
import {
    DEFAULT_ITEM_HEIGHT,
    INFINITE_LOADER_THRESHOLD,
    INFINITE_LOADER_MINIMUM_BATCH_SIZE,
    LIST_OVERSCAN_COUNT,
    LIST_WIDTH,
} from '../../pickerConstatns';
import styles from './BreadcrumbsDropdownMenu.module.scss';

const classNameBuilder = classnames.bind(styles);

export const BreadcrumbsDropdownMenu = forwardRef((props, ref) => {
    const {
        innerProps,
        inputValue = '',
        onInputChange,
        fetcher,
        onSelect,
        closeDropdown,
        allOption,
        ItemComponent,
        SearchComponent,
        SortComponent,
        selectedItem,
        queryKey,
        isOpen,
        children,
        onHeightResolved: _onHeightResolved,
        onDropdownClick,
        dataTestId,
        dataAutomationId,
        style,
        enabled,
        ...rest
    } = props;

    const intl = useIntl();
    const listRef = useRef(null);
    const itemHeights = useRef(new Map());

    const updateItemHeight = useCallback((index, height) => {
        if (height && height > 0) {
            const previousHeight = itemHeights.current.get(index);
            if (previousHeight !== height) {
                itemHeights.current.set(index, height);
                if (listRef.current) {
                    listRef.current.resetAfterIndex(index, false);
                }
            }
        }
    }, []);

    const getItemSize = index => {
        const height = itemHeights.current.get(index) || DEFAULT_ITEM_HEIGHT;
        return height;
    };

    const { options, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, refetch, error } = useDropdownData({
        fetcher,
        inputValue,
        queryKey,
        isOpen,
        allOption,
        enabled,
    });

    const { highlightedIndex, inputRef, handleInputChange, handleKeyDown } = useDropdownNavigation({
        isOpen,
        options,
        onSelect,
        closeDropdown,
        onInputChange,
        listRef,
    });

    const { ItemComponentRenderer, getListHeightWithVhConstraint, isItemLoaded, itemCount } = useDropdownRenderer({
        options,
        highlightedIndex,
        hasNextPage,
        ItemComponent,
        innerProps,
        onSelect,
        closeDropdown,
        selectedItem,
        isLoading,
        isFetchingNextPage,
        hasChildren: !!children,
        updateItemHeight,
        listRef,
        itemHeights,
        error,
        refetch,
        ...rest,
    });
    const loadMoreItems = useCallback(() => {
        if (fetchNextPage && hasNextPage) {
            fetchNextPage();
        }
    }, [fetchNextPage, hasNextPage]);

    return (
        <div
            className={classNameBuilder('dropdown-menu', 'dropdown-container', {
                'dropdown-visible': isOpen,
                'dropdown-hidden': !isOpen,
            })}
            ref={ref}
            onClick={onDropdownClick}
            data-testid={dataTestId}
            data-automation={dataAutomationId}
        >
            <div className={styles['dropdown-content-wrapper']} style={style}>
                <div className={`${styles['dropdown-header']} dropdown-menu__control`}>
                    {SearchComponent && (
                        <SearchComponent
                            inputValue={inputValue}
                            onInputChange={handleInputChange}
                            inputRef={inputRef}
                            onKeyDown={handleKeyDown}
                            SortComponent={SortComponent}
                            isOpen={isOpen}
                            {...rest}
                        />
                    )}

                    {children && <div className={styles['children-container']}>{children}</div>}
                </div>

                <div className="dropdown-menu-list">
                    {!isLoading ? (
                        isEmpty(options) && !isLoading ? (
                            <div className={styles['dropdown-empty']}>
                                {intl.formatMessage({
                                    id: 'app.dropdowns.no.options',
                                    defaultMessage: 'No options',
                                })}
                            </div>
                        ) : (
                            <InfiniteLoader
                                isItemLoaded={isItemLoaded}
                                itemCount={itemCount}
                                loadMoreItems={loadMoreItems}
                                threshold={INFINITE_LOADER_THRESHOLD}
                                minimumBatchSize={INFINITE_LOADER_MINIMUM_BATCH_SIZE}
                            >
                                {({ onItemsRendered, ref }) => (
                                    <List
                                        height={getListHeightWithVhConstraint()}
                                        width={LIST_WIDTH}
                                        itemCount={itemCount}
                                        itemSize={getItemSize}
                                        onItemsRendered={onItemsRendered}
                                        innerRef={ref}
                                        ref={listRef}
                                        overscanCount={LIST_OVERSCAN_COUNT}
                                    >
                                        {ItemComponentRenderer}
                                    </List>
                                )}
                            </InfiniteLoader>
                        )
                    ) : null}

                    {isEmpty(options) && (error || isLoading || isFetchingNextPage) && (
                        <div className={styles['dropdown-loading-container']}>
                            {error ? (
                                <RetryMenuItem
                                    onRetry={() => {
                                        if (refetch) {
                                            refetch();
                                        }
                                    }}
                                />
                            ) : (
                                <DropdownLoader />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});
